import {NetworkData} from "./interfaces";
import * as Sentry from "@sentry/react";
import {SeverityLevel} from "@sentry/react";
import {HubPathRouting} from "../HubFramework/pathBuilder";
import {getRefreshToken, getToken, hasToken, openLoginPage, setTokensFromHeader} from "../Utils/authentication";
import {backendApiURL} from "../Config/environmentVars";
import {useMain} from "../Utils/SessionControls/mainContext";

// const {createErrorToast} = useMain(); //todo: Niklas why

export function getBackendApiURL() {
    if(backendApiURL.indexOf('{') !== -1) {
        // return "https://func-devsecopshub-stage.azurewebsites.net/api/"
        return window.location.origin + "/api/"
    }
    return backendApiURL
}

/**
 * Returns the backend api url for the current project and module instance.
 * Useful for calling the backend api from a module.
 */
export function getBackendApiURLModuleInstance() {
    return getBackendApiURL() + "projects/" + HubPathRouting.currentProject.id + "/modules/" + HubPathRouting.currentModule.id + "/"
}

async function parseResponseBody(response: Response) {
    if (response.status === 204 || response.status === 205) {
        return undefined;
    }

    const text = await response.text();
    if (!text) {
        return undefined;
    }

    const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
    if (contentType.includes("application/json")) {
        try {
            return JSON.parse(text);
        } catch (error) {
            return text;
        }
    }

    return text;
}

/**
 * Main backend facade function to call the backend api. Returns a promise with the response, success and message.
 * @param url The url to call
 * @param method HTTP method: GET, POST, PUT, DELETE, PATCH
 * @param body body
 * @param contentType Content-Type of the request's header
 * @param file File to be uploaded
 */
export async function fetchFromApi(
    url: string,
    method: "POST" | "GET" | "PUT" | "DELETE" | "PATCH",
    body: { [key: string] : any } | string = null,
    contentType = "application/json",
    file: File = null
    ): Promise<NetworkData> {
    const headers: Record<string, string> = {
            "Accept": "application/json",
            "Content-Type": contentType,
            "id_token" : hasToken() ? getToken(): null,
            "refresh_token" : getRefreshToken(),
            "x-frontend-url" : window.location.origin
    };

    const config: RequestInit = {
        method: method,
        headers
    }

    if(body && contentType === "application/json") {
        config["body"] = JSON.stringify(body)
    }
    if(body && contentType === "application/x-www-form-urlencoded") {
        config.body = body as string;
    }
    if (file) {
        delete headers["Content-Type"];
        const formData = new FormData();
        formData.append('File', file, file.name);
        if (config["body"]) {
            Object.keys(body).forEach((key) =>  {
                const value = (body as {[p: string]: any})[key]
                formData.append(key, value)
            })
        }
        config["body"] = formData
    }
    try {
        return fetch(url, config).then(async function(response) {
            const ret: NetworkData = {data: {value: undefined}, message: "", success: true};
            // case 2xx, success
            if (response.ok) {
                const parsedBody = await parseResponseBody(response);
                if (parsedBody !== undefined) {
                    ret.data = parsedBody;
                }
                setTokensFromHeader(response)
                return ret
            }

            // error or warning case, define success and message
            ret.success = false
            ret.message = response.status + ": " + await response.text()

            // if the keywords 'Error refreshing token' are in the error message, revoke the token
            // send the user to the login page
            if (ret.message.indexOf("Error refreshing token") !== -1) {
                openLoginPage()
                return ret
            }

            // base level for non 500 error -> Warning level for sentry
            let level: SeverityLevel = "warning"

            // case 5xx, backend error -> Error level for sentry
            if (response.status >= 500) {
                level = "error"
            }

            // log message and level to sentry
            Sentry.captureMessage("Message: " + ret.message, level)

            return ret;
        })
            // return finalised NetworkData Object
            .then(function(response: NetworkData) {
                Sentry.captureMessage("Message: " + response, "info")
                return response;
            })
            // unexpected error, return empty NetworkData Object
            .catch(function(err) {
                const ret : NetworkData = {message: err.toString(), success: false, data: {value: undefined}}
                // Log to sentry
                Sentry.captureMessage("Message: " + err, "error")
                return ret;
            })
    } catch (e) {
        const ret : NetworkData = {message: e, success: false, data: {value: undefined}}
        Sentry.captureMessage("fatal Message: " + e, "error")
        return ret;
    }

}

export function fetchModuleData <T>(
    endpoint: string,
    method: "POST" | "GET" | "PUT" | "DELETE" | "PATCH",
    body: { [key: string] : any } | string = null,
    contentType = "application/json",
    file: File = null
    ): Promise<T> {
    const url = "projects/" + HubPathRouting.currentProject.id + "/modules/" + HubPathRouting.currentModule.id + "/" + endpoint
    return fetchAndHandle<T>(url, method, body, contentType, file)
}

export async function fetchAndHandle<T>(
    url: string,
    method: "POST" | "GET" | "PUT" | "DELETE" | "PATCH",
    body: { [key: string]: any } | string = null,
    contentType = "application/json",
    file: File = null,
    doErrorHandling = true
): Promise<T> {
    url = getBackendApiURL() + url
    const response = await fetchFromApi(url, method, body, contentType, file)
    if (!response.success && doErrorHandling) {
        //createErrorToast(response.message, "An Error occurred")
        console.error("Hier könnte ihr fehler stehen")
        throw new Error(response.message)
    }
    return response.data

}
